import React, { useState, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { ScanFace, MapPin, Shield, ArrowRight, Building2, Users, ChevronLeft, KeyRound, Phone, LogIn } from "lucide-react";

// Steps:
//  choice               — pick New Company vs Existing Company
//  signup               — register a NEW company (multi-tenant, always available)
//  mobile               — enter mobile (existing company login)
//  pin-login            — enter PIN (returning user)
//  pin-setup            — set PIN first time (employee pre-created)
//  mobile-not-registered

export default function Login() {
  const nav = useNavigate();
  const { refresh } = useAuth();
  const [params] = useSearchParams();

  const initialStep = params.get("mode") === "new" ? "signup" : params.get("mode") === "login" ? "mobile" : "choice";
  const [step, setStep] = useState(initialStep);
  const [mobile, setMobile] = useState("");
  const [pin, setPin] = useState("");
  const [confirmPin, setConfirmPin] = useState("");
  const [matchedName, setMatchedName] = useState("");
  const [busy, setBusy] = useState(false);

  // signup form
  const [su, setSu] = useState({ name: "", mobile: "", pin: "", confirmPin: "", companyName: "", employeeCount: "5" });

  const checkMobile = async () => {
    if (busy) return;
    const m = mobile.trim();
    if (!m) { toast.error("Enter mobile"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/auth/check-mobile", { mobile: m });
      if (!data.exists) {
        setStep("mobile-not-registered");
      } else if (data.needs_pin_setup) {
        setMatchedName(data.name);
        setPin(""); setConfirmPin("");
        setStep("pin-setup");
      } else {
        setMatchedName(data.name);
        setPin("");
        setStep("pin-login");
      }
    } catch (e) {
      toast.error(formatErr(e));
    } finally { setBusy(false); }
  };

  const doLogin = async () => {
    if (busy) return;
    if (!/^\d{4,6}$/.test(pin)) { toast.error("PIN is 4–6 digits"); return; }
    setBusy(true);
    try {
      await api.post("/auth/login", { mobile, pin });
      await refresh();
      toast.success("Welcome back!");
      nav("/", { replace: true });
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(false); }
  };

  const doSetPin = async () => {
    if (busy) return;
    if (!/^\d{4,6}$/.test(pin)) { toast.error("PIN must be 4–6 digits"); return; }
    if (pin !== confirmPin) { toast.error("PINs don't match"); return; }
    setBusy(true);
    try {
      await api.post("/auth/set-pin", { mobile, pin });
      await refresh();
      toast.success("PIN set. Welcome!");
      nav("/", { replace: true });
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(false); }
  };

  const doSignup = async () => {
    if (busy) return;
    if (!su.name.trim()) { toast.error("Enter your name"); return; }
    if (!su.companyName.trim()) { toast.error("Enter company name"); return; }
    if (!/^[\d+]{7,15}$/.test(su.mobile.replace(/\s/g, ""))) { toast.error("Enter a valid mobile"); return; }
    if (!/^\d{4,6}$/.test(su.pin)) { toast.error("PIN must be 4–6 digits"); return; }
    if (su.pin !== su.confirmPin) { toast.error("PINs don't match"); return; }
    setBusy(true);
    try {
      await api.post("/auth/signup", {
        name: su.name.trim(),
        mobile: su.mobile,
        pin: su.pin,
        company_name: su.companyName.trim(),
        employee_count: parseInt(su.employeeCount || "0", 10),
      });
      await refresh();
      toast.success("Welcome aboard!");
      nav("/", { replace: true });
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(false); }
  };

  return (
    <div
      className="mx-auto w-full max-w-md min-h-[100svh] flex flex-col relative overflow-hidden"
      style={{ background: "#0A0D14" }}
      data-testid="login-page"
    >
      <div className="absolute inset-0 opacity-30 geo-grid pointer-events-none" />
      <div className="absolute -top-32 -right-24 w-72 h-72 rounded-full pointer-events-none"
        style={{ background: "radial-gradient(circle, rgba(79,142,247,0.35), transparent 70%)" }} />
      <div className="absolute -bottom-32 -left-24 w-72 h-72 rounded-full pointer-events-none"
        style={{ background: "radial-gradient(circle, rgba(34,211,165,0.25), transparent 70%)" }} />

      <div className="relative flex-1 flex flex-col px-6 pt-12 pb-10 pt-safe">
        <Brand onHome={() => nav("/welcome")} />

        {step === "choice" && (
          <ChoiceStep
            onNew={() => setStep("signup")}
            onExisting={() => setStep("mobile")}
          />
        )}
        {step === "signup" && <SignupForm su={su} setSu={setSu} busy={busy} onSubmit={doSignup} onBack={() => setStep("choice")} />}
        {step === "mobile" && (
          <MobileForm mobile={mobile} setMobile={setMobile} busy={busy} onSubmit={checkMobile} onBack={() => setStep("choice")} />
        )}
        {step === "pin-login" && (
          <PinForm
            title={`Hi, ${matchedName.split(" ")[0]}`}
            subtitle={`Enter PIN for ${maskMobile(mobile)}`}
            pin={pin} setPin={setPin}
            cta="Login" busy={busy} onSubmit={doLogin}
            onBack={() => setStep("mobile")}
            testid="login-pin"
          />
        )}
        {step === "pin-setup" && (
          <PinSetupForm
            name={matchedName} mobile={mobile}
            pin={pin} setPin={setPin}
            confirmPin={confirmPin} setConfirmPin={setConfirmPin}
            busy={busy} onSubmit={doSetPin}
            onBack={() => setStep("mobile")}
          />
        )}
        {step === "mobile-not-registered" && (
          <NotRegistered mobile={mobile} onBack={() => setStep("mobile")} />
        )}

        <div className="mt-auto text-[10px] uppercase tracking-widest text-center" style={{ color: "#7A85A3" }}>
          v1.1 · Mobile + PIN · Made for mobile
        </div>
      </div>
    </div>
  );
}

function Brand({ onHome }) {
  return (
    <button onClick={onHome} className="flex items-center gap-2 mb-10 self-start" data-testid="login-brand">
      <div className="w-10 h-10 rounded-xl flex items-center justify-center font-syne font-bold text-lg"
        style={{ background: "#4F8EF7", color: "#fff" }}>S</div>
      <div className="text-left">
        <div className="font-syne font-bold tracking-tight" style={{ color: "#E8ECFF" }}>
          Swaagat
        </div>
        <div className="text-[10px] uppercase tracking-widest" style={{ color: "#7A85A3" }}>Attendance</div>
      </div>
    </button>
  );
}

function ChoiceStep({ onNew, onExisting }) {
  return (
    <>
      <div className="text-[11px] uppercase tracking-[0.3em] mb-3" style={{ color: "#7A85A3" }}>
        Workforce · Biometric · Geo
      </div>
      <h1 className="font-syne text-4xl sm:text-5xl font-bold tracking-tight leading-[1.05] mb-3" style={{ color: "#E8ECFF" }}>
        Get started<br />with Swaagat.
      </h1>
      <p className="text-sm mb-8" style={{ color: "#7A85A3" }}>
        Register a new company or sign in to an existing one.
      </p>

      <button
        onClick={onNew}
        data-testid="choice-new-company"
        className="w-full rounded-2xl p-5 mb-3 text-left flex items-center gap-4 active:scale-[0.99] transition-transform"
        style={{ background: "rgba(34,211,165,0.08)", border: "1.5px solid rgba(34,211,165,0.4)" }}
      >
        <div className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0" style={{ background: "rgba(34,211,165,0.15)" }}>
          <Building2 size={22} style={{ color: "#22D3A5" }} />
        </div>
        <div className="flex-1">
          <div className="font-bold" style={{ color: "#E8ECFF" }}>New Company</div>
          <div className="text-xs mt-0.5" style={{ color: "#7A85A3" }}>Register your company & become admin</div>
        </div>
        <ArrowRight size={18} style={{ color: "#22D3A5" }} />
      </button>

      <button
        onClick={onExisting}
        data-testid="choice-existing-login"
        className="w-full rounded-2xl p-5 text-left flex items-center gap-4 active:scale-[0.99] transition-transform"
        style={{ background: "rgba(79,142,247,0.08)", border: "1.5px solid rgba(79,142,247,0.4)" }}
      >
        <div className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0" style={{ background: "rgba(79,142,247,0.15)" }}>
          <LogIn size={22} style={{ color: "#4F8EF7" }} />
        </div>
        <div className="flex-1">
          <div className="font-bold" style={{ color: "#E8ECFF" }}>Existing Company</div>
          <div className="text-xs mt-0.5" style={{ color: "#7A85A3" }}>Login with your mobile number & PIN</div>
        </div>
        <ArrowRight size={18} style={{ color: "#4F8EF7" }} />
      </button>

      <div className="grid grid-cols-3 gap-2 mt-8">
        {[
          { icon: ScanFace, label: "Face Scan" },
          { icon: MapPin, label: "Geo Verify" },
          { icon: Shield, label: "Private" },
        ].map((f) => (
          <div key={f.label} className="rounded-2xl p-3 text-center" style={{ background: "#161B2C", border: "1px solid #1E2640" }}>
            <f.icon size={20} className="mx-auto mb-1.5" style={{ color: "#4F8EF7" }} />
            <div className="text-[11px] font-medium" style={{ color: "#E8ECFF" }}>{f.label}</div>
          </div>
        ))}
      </div>
    </>
  );
}

function MobileForm({ mobile, setMobile, busy, onSubmit, onBack }) {
  return (
    <>
      <button onClick={onBack} className="self-start mb-4 flex items-center gap-1 text-sm" style={{ color: "#7A85A3" }} data-testid="back-to-choice">
        <ChevronLeft size={16} /> Back
      </button>
      <div className="text-[11px] uppercase tracking-[0.3em] mb-3" style={{ color: "#4F8EF7" }}>
        Existing Company
      </div>
      <h1 className="font-syne text-4xl sm:text-5xl font-bold tracking-tight leading-[1.05] mb-3" style={{ color: "#E8ECFF" }}>
        Welcome<br />back.
      </h1>
      <p className="text-sm mb-8" style={{ color: "#7A85A3" }}>
        Sign in with your mobile number and PIN.
      </p>

      <FieldLabel icon={Phone}>Mobile Number</FieldLabel>
      <input
        type="tel"
        inputMode="tel"
        autoComplete="tel"
        value={mobile}
        onChange={(e) => setMobile(e.target.value)}
        onKeyDown={(e) => e.key === "Enter" && onSubmit()}
        placeholder="e.g. 919876543210"
        data-testid="mobile-input"
        className="w-full h-12 px-4 rounded-xl text-base font-mono mb-3"
        style={{ background: "#161B2C", border: "1px solid #1E2640", color: "#E8ECFF" }}
      />

      <button
        onClick={onSubmit}
        disabled={busy}
        data-testid="mobile-continue-button"
        className="w-full h-12 rounded-xl font-bold flex items-center justify-center gap-2 active:scale-[0.99] transition-transform disabled:opacity-50"
        style={{ background: "#4F8EF7", color: "#fff" }}
      >
        Continue <ArrowRight size={18} />
      </button>

      <div className="text-[11px] text-center mt-4" style={{ color: "#7A85A3" }}>
        New employee? Ask your admin to add your mobile first.
      </div>
    </>
  );
}

function PinForm({ title, subtitle, pin, setPin, busy, onSubmit, onBack, cta, testid }) {
  return (
    <>
      <button onClick={onBack} className="self-start mb-4 flex items-center gap-1 text-sm" style={{ color: "#7A85A3" }} data-testid="back-to-mobile">
        <ChevronLeft size={16} /> Back
      </button>
      <h1 className="font-syne text-4xl font-bold tracking-tight leading-[1.05] mb-1" style={{ color: "#E8ECFF" }}>{title}</h1>
      <p className="text-sm mb-8" style={{ color: "#7A85A3" }}>{subtitle}</p>

      <FieldLabel icon={KeyRound}>PIN (4–6 digits)</FieldLabel>
      <PinInput value={pin} onChange={setPin} onEnter={onSubmit} testid={testid || "pin-input"} />

      <button
        onClick={onSubmit}
        disabled={busy || pin.length < 4}
        data-testid="pin-submit-button"
        className="w-full h-12 rounded-xl font-bold mt-6 flex items-center justify-center gap-2 disabled:opacity-50"
        style={{ background: "#4F8EF7", color: "#fff" }}
      >
        {cta} <ArrowRight size={18} />
      </button>
    </>
  );
}

function PinSetupForm({ name, mobile, pin, setPin, confirmPin, setConfirmPin, busy, onSubmit, onBack }) {
  return (
    <>
      <button onClick={onBack} className="self-start mb-4 flex items-center gap-1 text-sm" style={{ color: "#7A85A3" }} data-testid="back-to-mobile">
        <ChevronLeft size={16} /> Back
      </button>
      <h1 className="font-syne text-3xl font-bold tracking-tight leading-[1.05] mb-1" style={{ color: "#E8ECFF" }}>Welcome, {name.split(" ")[0]}</h1>
      <p className="text-sm mb-6" style={{ color: "#7A85A3" }}>
        First time on {maskMobile(mobile)} — set your PIN to continue.
      </p>

      <FieldLabel icon={KeyRound}>Create PIN</FieldLabel>
      <PinInput value={pin} onChange={setPin} testid="setup-pin-input" />

      <FieldLabel icon={KeyRound} className="mt-4">Confirm PIN</FieldLabel>
      <PinInput value={confirmPin} onChange={setConfirmPin} onEnter={onSubmit} testid="setup-confirm-pin-input" />

      <button
        onClick={onSubmit}
        disabled={busy || pin.length < 4 || confirmPin.length < 4}
        data-testid="setup-pin-submit"
        className="w-full h-12 rounded-xl font-bold mt-6 disabled:opacity-50"
        style={{ background: "#22D3A5", color: "#0A0D14" }}
      >
        Set PIN & Continue
      </button>
    </>
  );
}

function SignupForm({ su, setSu, busy, onSubmit, onBack }) {
  const set = (k, v) => setSu((s) => ({ ...s, [k]: v }));
  const inputCls = "w-full h-12 px-4 rounded-xl text-base mb-3";
  const inputStyle = { background: "#161B2C", border: "1px solid #1E2640", color: "#E8ECFF" };
  return (
    <>
      <button onClick={onBack} className="self-start mb-4 flex items-center gap-1 text-sm" style={{ color: "#7A85A3" }} data-testid="back-to-choice-signup">
        <ChevronLeft size={16} /> Back
      </button>
      <div className="text-[11px] uppercase tracking-[0.3em] mb-2" style={{ color: "#22D3A5" }}>
        New Company
      </div>
      <h1 className="font-syne text-3xl font-bold tracking-tight leading-[1.05] mb-2" style={{ color: "#E8ECFF" }}>
        Set up your company
      </h1>
      <p className="text-sm mb-6" style={{ color: "#7A85A3" }}>
        You'll be the admin. You can add employees after.
      </p>

      <FieldLabel icon={Building2}>Company Name</FieldLabel>
      <input data-testid="signup-company" value={su.companyName} onChange={(e) => set("companyName", e.target.value)} placeholder="e.g. Acme Inc." className={inputCls} style={inputStyle} />

      <FieldLabel icon={Users}>How many employees?</FieldLabel>
      <input data-testid="signup-employees" type="number" min="1" value={su.employeeCount} onChange={(e) => set("employeeCount", e.target.value)} placeholder="5" className={inputCls} style={inputStyle} />

      <div className="rounded-2xl p-4 mb-3 mt-2" style={{ background: "rgba(79,142,247,0.06)", border: "1px solid rgba(79,142,247,0.2)" }}>
        <div className="text-[10px] uppercase tracking-widest mb-3" style={{ color: "#4F8EF7" }}>Admin Account</div>
        <FieldLabel>Your Name</FieldLabel>
        <input data-testid="signup-name" value={su.name} onChange={(e) => set("name", e.target.value)} placeholder="Jane Doe" className={inputCls} style={inputStyle} />
        <FieldLabel icon={Phone}>Mobile Number</FieldLabel>
        <input data-testid="signup-mobile" type="tel" inputMode="tel" value={su.mobile} onChange={(e) => set("mobile", e.target.value)} placeholder="919876543210" className={inputCls + " font-mono"} style={inputStyle} />
        <FieldLabel icon={KeyRound}>Create PIN (4–6 digits)</FieldLabel>
        <PinInput value={su.pin} onChange={(v) => set("pin", v)} testid="signup-pin" />
        <FieldLabel icon={KeyRound} className="mt-3">Confirm PIN</FieldLabel>
        <PinInput value={su.confirmPin} onChange={(v) => set("confirmPin", v)} testid="signup-confirm-pin" onEnter={onSubmit} />
      </div>

      <button
        onClick={onSubmit}
        disabled={busy}
        data-testid="signup-submit"
        className="w-full h-12 rounded-xl font-bold flex items-center justify-center gap-2 disabled:opacity-50"
        style={{ background: "#22D3A5", color: "#0A0D14" }}
      >
        Create Company <ArrowRight size={18} />
      </button>
    </>
  );
}

function NotRegistered({ mobile, onBack }) {
  return (
    <>
      <button onClick={onBack} className="self-start mb-4 flex items-center gap-1 text-sm" style={{ color: "#7A85A3" }}>
        <ChevronLeft size={16} /> Back
      </button>
      <div className="rounded-2xl p-5" style={{ background: "rgba(247,201,79,0.08)", border: "1px solid rgba(247,201,79,0.3)" }}>
        <div className="text-[11px] uppercase tracking-widest mb-2" style={{ color: "#F7C94F" }}>Not Registered</div>
        <h2 className="font-syne text-xl font-bold tracking-tight mb-2" style={{ color: "#E8ECFF" }}>
          {maskMobile(mobile)} isn't on your team yet.
        </h2>
        <p className="text-sm" style={{ color: "#7A85A3" }}>
          Ask your admin to add your mobile in the Team page. Then come back and tap Continue.
        </p>
      </div>
    </>
  );
}

function FieldLabel({ icon: Icon, children, className = "" }) {
  return (
    <label className={`text-[10px] uppercase tracking-widest mb-1.5 flex items-center gap-1.5 ${className}`} style={{ color: "#7A85A3" }}>
      {Icon && <Icon size={11} />} {children}
    </label>
  );
}

function PinInput({ value, onChange, onEnter, testid }) {
  const inputRef = useRef(null);
  const digits = value.padEnd(6, " ").slice(0, 6).split("");
  return (
    <div className="relative">
      <input
        ref={inputRef}
        type="tel"
        inputMode="numeric"
        pattern="\d*"
        maxLength={6}
        value={value}
        onChange={(e) => onChange(e.target.value.replace(/\D/g, "").slice(0, 6))}
        onKeyDown={(e) => e.key === "Enter" && onEnter && onEnter()}
        data-testid={testid}
        className="absolute inset-0 w-full h-full opacity-0"
        autoFocus
      />
      <div className="grid grid-cols-6 gap-2" onClick={() => inputRef.current?.focus()}>
        {digits.map((d, i) => (
          <div key={i}
            className="h-12 rounded-xl flex items-center justify-center font-mono text-xl font-bold"
            style={{
              background: "#161B2C",
              border: `1px solid ${i === value.length ? "#4F8EF7" : "#1E2640"}`,
              color: "#E8ECFF",
            }}>
            {d.trim() ? "•" : ""}
          </div>
        ))}
      </div>
    </div>
  );
}

function maskMobile(m) {
  if (!m) return "";
  if (m.length <= 4) return m;
  return m.slice(0, -4).replace(/./g, "•") + m.slice(-4);
}

function formatErr(e) {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x.msg || JSON.stringify(x)).join(" ");
  return e?.message || "Something went wrong";
}
